/**
 * tocEntries — derive a numbered Table of Contents from a report's
 * section order, visibility map, and heading overrides.
 *
 * Inputs:
 *   - sectionOrder: ordered list of sectionIds. Defaults to the
 *     type's canonical list when undefined.
 *   - sectionVisibility: per-id visibility flag. Missing → visible.
 *   - reportOverrides / templateOverrides: per-id heading text.
 *     Resolution chain mirrors `resolveHeading` from
 *     src/config/reportSections.ts (per-report > per-template >
 *     defaultLabel).
 *
 * Output: one entry per VISIBLE section. The TOC component renders
 * them with 1-based numbering. Sections without a registered label
 * are dropped — they're typically diagnostic blocks (verdict badge,
 * cover page) that don't deserve their own TOC entry.
 *
 * Pure / side-effect free so the entry derivation can be tested
 * without mounting any of the report tree.
 */
import type { ReportTypeId } from '../config/reportTypes';
import { hiddenReportSectionIds } from './reportGeneration/sectionOmissions';
import { getSectionsForType, resolveHeading } from '../config/reportSections';
import { rendererSectionById } from '../config/rendererSectionRegistry';
import type { CustomBriefAnswer, SiteRagReport } from '../types/siteRag';
import { isAllowedExtraSectionId } from './effectiveTemplateConfig';
import {
  isCustomBriefQuestionSectionId,
  parseCustomBriefQuestionSectionId,
  customBriefQuestionTocLabel,
  resolveCustomBriefQuestionNumber,
} from './customBriefSections';
import { isSectionApplicableForReportType } from './isSectionEnabledForType';
import { fieldPathForSection, getReportFieldValue, hasSectionFieldContent } from './sectionFieldPaths';
import { summarizeIntentFeasibility } from './phaseFeasibility';
import { gridLedStandbyPlanningReportHasContent } from './generationImpact/gridLedStandbyPlanningReportContent';

/** Sections that exist for layout purposes but should not appear as
 *  individual TOC entries — they're either redundant with the
 *  document's title (cover) or sub-blocks of a larger section
 *  (verdict ribbon under the cover). Adding to this set requires no
 *  schema change because the registry already lists these IDs; they
 *  just don't earn their own TOC line. */
const TOC_HIDDEN_IDS = new Set<string>([
  'toc',
  'end',
  'verdict',
  'categoryScoresTable',
  'btmScatter',
  'scoreDrivers',
  'dataQualityNotes',
  'scoreProvenance',
  'blindSpots',
]);

/**
 * Per-section "does the report have data for this section?" check.
 * Mirrors the body renderer's guards in SiteRagReportView so the TOC
 * and the body agree on what exists. Sections without an entry here
 * default to `() => true` (registry-membership alone is enough) —
 * the registry lists every section the body knows how to render, so
 * the default is the right back-stop.
 *
 * Keep this lookup in sync with the body's per-section render gates
 * when adding new section types; the bidirectional TOC↔body test
 * (effectiveTocEntries.bodyAgreement.test) will fail if they drift.
 */
type RenderableCheck = (report: Record<string, unknown>) => boolean;

/**
 * Optional On-Site Generation Test Fit exhibit gate. The complete exhibit is
 * renderable from its immutable snapshot. A prior canonical generation-impact
 * snapshot also earns a body/TOC entry because the section renders a precise
 * regenerate-to-freeze-layout accountability state for those older revisions.
 * The unauthenticated compositor receives only the coordinate-free public
 * ledger, which is sufficient to retain the hash-bound accountability section
 * without exposing parcel or CAD geometry. Reports with none of these frozen
 * inputs omit the optional section entirely.
 */
export function generationLayoutExhibitHasContent(
  report: Record<string, unknown>,
): boolean {
  return !!report.generationLayoutExhibitAtGen
    || !!report.generationLayoutExhibitPublicAtGen
    || !!report.generationImpactAtGen;
}

/**
 * The promoted-vs-rejected comparison exists only when the engineer actually
 * compared two or more configurations. A comparison of one is not a comparison,
 * so the section — and its TOC entry — is omitted rather than rendered empty.
 */
export function generationScenarioComparisonHasContent(
  report: Record<string, unknown>,
): boolean {
  const projection = report.generationImpactProjection as
    { comparison?: { rows?: unknown[] } } | undefined;
  return (projection?.comparison?.rows?.length ?? 0) >= 2;
}

/** Modeled water belongs to the saved revision. Historical reports without
 * that optional disclosure must not acquire an empty body or contents entry. */
export function waterScreeningHasContent(report: Pick<SiteRagReport, 'waterScreening'> | Record<string, unknown>): boolean {
  return typeof report.waterScreening === 'string' && report.waterScreening.trim().length > 0;
}

/**
 * Sections the body always renders (either with real content or a
 * deterministic skeleton) and that should therefore always appear in
 * the TOC. TNV-2: the previous per-section data presence gates in
 * `SECTION_CONTENT_CHECKS` would strip these entries from the TOC
 * when the report ran in degradedMode and the typed field was falsy,
 * even though the body still rendered the heading. Sections in this
 * set bypass the content check — they're gated solely on
 * `sectionVisibility` (TSE-2 C8 plumbing). Keep narrow: only the
 * canonical "always shown" sections belong here; truly optional
 * sections (custom inserts, footer markers, dataQualityNotes) keep
 * their existing content gates so they don't pollute the TOC when
 * empty.
 */
const ALWAYS_TOC_SECTIONS = new Set<string>([
  'executiveSummary',
  'projectContext',
  'projectIntentOverview',
  'btmOverview',
  'sitingNotes',
  'climateSevereWeather',
  'gridStudy',
  'transportStudy',
  'connectivityStudy',
  'climateProfile',
  'riskAssessment',
  'actionPlan',
  'radarChart',
  'lcoeChart',
  'extendedData',
  'sentimentResearchDetail',
  'recommendations',
  'conclusion',
  'appendix',
  // PSR + modern per-type sections — body always renders heading + placeholder (RTB-2)
  'executive',
  'stakeholderMap',
  'perBtmSentiment',
  'themeDeepDives',
  'trendAnalysis',
  'comparableSiteBenchmark',
  'mitigationPlaybook',
  'gridCapacity', 'interconnectionQueue', 'curtailmentRisk', 'rateStructures',
  'powerStrategyContext', 'renewableIntegration', 'gridRedundancy',
  'btmTechComparison', 'lcoeBreakdown', 'gridVsBtm', 'powerRiskMatrix',
  'supplyAvailability', 'groundwater', 'waterRights', 'droughtResilience',
  'coolingByTech', 'wastewaterStormwater', 'conservation', 'totalWaterCost', 'waterRiskMatrix',
  'parcelOverview', 'topography', 'buildingPlacement', 'setbackCompliance',
  'parkingAccess', 'stormwater', 'utilityRouting', 'phasedBuildout', 'maxCapacity',
  'constructabilityAssessment', 'costRangeEstimate', 'riskMatrix',
  'fiberRings', 'latencyModel', 'carrierDiversity', 'ixProximity', 'submarineCable',
  'networkResilience', 'bandwidthCapacity', 'capexOpex', 'connectivityRiskMatrix',
  'nepaScreening', 'ecologicalAssessment', 'waterResources', 'airQualityEmissions',
  'floodNaturalHazards', 'culturalHistorical', 'landUseZoning', 'noiseVisualImpact',
  'mitigationPlan', 'alternativesAnalysis', 'cumulativeImpacts', 'publicParticipationPlan',
  'permittingPathway', 'environmentalRiskMatrix',
  // Executive Summary cross-report brief
  'btmViability', 'crossReportSynthesis', 'criticalRisks', 'sourceReports',
  // Supply Chain Risk
  'procurementOverview', 'criticalPathProcurement', 'supplyChainRisks',
  'supplierRiskRanking', 'quoteComparisons', 'supplyChainRiskMatrix',
]);

const SECTION_CONTENT_CHECKS: Record<string, RenderableCheck> = {
  // ── Feasibility ─────────────────────────────────────────────────
  executiveSummary:        r => typeof r.executiveSummary === 'string' && (r.executiveSummary as string).trim().length > 0,
  projectIntentOverview:   r => typeof r.projectIntentOverview === 'string' && (r.projectIntentOverview as string).trim().length > 0
                              || !!r.projectIntentAtGen
                              || (Array.isArray(r.structuredFindings) && (r.structuredFindings as unknown[]).length > 0),
  btmOverview:             r => Array.isArray(r.btmOptions) && (r.btmOptions as unknown[]).length > 0,
  gridStudy:               r => !!r.gridStudy,
  transportStudy:          r => !!r.transportStudy,
  connectivityStudy:       r => !!r.connectivityStudy,
  sitingNotes:             r => typeof r.sitingNotes === 'string' && (r.sitingNotes as string).trim().length > 0,
  climateSevereWeather:    r => !!r.climateSevereWeather && (
    typeof r.climateSevereWeather === 'string'
      ? (r.climateSevereWeather as string).trim().length > 0
      : true
  ),
  climateProfile:          r => Array.isArray(r.timeSeries) && (r.timeSeries as unknown[]).length > 0,
  riskAssessment:          r => ((r.developmentRisks as unknown[] | undefined)?.length ?? 0) > 0
                              || ((r.risks as unknown[] | undefined)?.length ?? 0) > 0,
  actionPlan:              r => ((r.developmentPathway as unknown[] | undefined)?.length ?? 0) > 0
                              || ((r.recommendations as unknown[] | undefined)?.length ?? 0) > 0,
  radarChart:              r => Array.isArray(r.categories) && (r.categories as unknown[]).length > 0,
  lcoeChart:               r => Array.isArray(r.btmOptions) && (r.btmOptions as unknown[]).length > 0,
  extendedData:            r => !!r.datasets,
  sentimentResearchDetail: r => !!r.publicSentimentMeta || !!r.datasets,
  conclusion:              r => !!r.conclusion,
  appendix:                r => Array.isArray(r.references) && (r.references as unknown[]).length > 0,
  // ── Environmental ───────────────────────────────────────────────
  nepaScreening:           r => !!r.nepaScreening,
  ecologicalAssessment:    r => !!r.ecologicalAssessment,
  waterResources:          r => !!r.waterResources,
  airQualityEmissions:     r => !!r.airQualityEmissions,
  floodNaturalHazards:     r => !!r.floodNaturalHazards,
  culturalHistorical:      r => !!r.culturalHistorical,
  landUseZoning:           r => !!r.landUseZoning,
  noiseVisualImpact:       r => !!r.noiseVisualImpact,
  mitigationPlan:          r => !!r.mitigationPlan,
  permittingPathway:       r => !!r.permittingPathway,
  environmentalRiskMatrix: r => !!r.environmentalRiskMatrix,
  // ── Other report types ──────────────────────────────────────────
  powerStrategyContext:    r => {
    const ctx = r.powerStrategyContext as { requirementsSummary?: string; strategyPosture?: string; analysisScope?: string; keyDecisionCriteria?: unknown[] } | undefined;
    return !!(ctx?.requirementsSummary?.trim() || ctx?.strategyPosture?.trim() || ctx?.analysisScope?.trim() || (ctx?.keyDecisionCriteria?.length ?? 0) > 0);
  },
  renewableIntegration:    r => !!(r.powerAnalysis as { renewableIntegration?: string } | undefined)?.renewableIntegration?.trim(),
  gridRedundancy:          r => !!(r.powerAnalysis as { redundancyAssessment?: string } | undefined)?.redundancyAssessment?.trim(),
  powerAnalysis:           r => !!r.powerAnalysis,
  btmDetailedAnalysis:     r => Array.isArray(r.btmDetailedAnalysis) && (r.btmDetailedAnalysis as unknown[]).length > 0,
  gridVsBtmComparison:     r => !!r.gridVsBtmComparison,
  lcoeDetailedAnalysis:    r => Array.isArray(r.lcoeDetailedAnalysis) && (r.lcoeDetailedAnalysis as unknown[]).length > 0,
  waterAnalysis:           r => !!r.waterAnalysis,
  testFitAnalysis:         r => !!r.testFitAnalysis,
  interconnectionAnalysis: r => !!r.interconnectionAnalysis,
  // Optional Test Fit exhibit: include it in the numbered contents only when
  // the report revision carries either the complete immutable exhibit or the
  // older frozen generation snapshot that the body turns into an explicit
  // regenerate-to-add-layout accountability state. Never inspect live layers.
  generationLayoutExhibit: generationLayoutExhibitHasContent,
  generationScenarioComparison: generationScenarioComparisonHasContent,
  gridLedStandbyPlanning: gridLedStandbyPlanningReportHasContent,
  waterScreening: waterScreeningHasContent,
};

/**
 * Capacity-Credit & Firm Adequacy (ELCC) — the BODY renders on a BROADER
 * predicate than its single typed field `powerAnalysis.capacityCreditDeepDive`:
 * CapacityCreditDeepDiveSection ALSO shows the deterministic power-basis /
 * firm-deliverable strip whenever the intent has assessed phases or a declared
 * power requirement (its `hasData`). This shared predicate mirrors that `hasData`
 * exactly so the three render decisions agree: the TOC/numbering
 * (`hasRenderableContent`) and the router's bare-heading skip-guard
 * (SectionsRouter) both key on it — the section is numbered IFF the body renders
 * it. The `summarizeIntentFeasibility` roll-up is advisory and guarded the same
 * way the body guards it, so it can never throw into the TOC loop or a render pass.
 */
export function capacityCreditDeepDiveHasContent(
  report: Pick<SiteRagReport, 'powerAnalysis' | 'projectIntentAtGen' | 'generationImpactProjection'>,
): boolean {
  const intent = report.projectIntentAtGen;
  const assessedCount = (() => {
    try { return summarizeIntentFeasibility(intent)?.assessedCount ?? 0; }
    catch { return 0; }
  })();
  return (
    !!report.powerAnalysis?.capacityCreditDeepDive
    || assessedCount > 0
    || !!intent?.powerRequirementMW
    || !!report.generationImpactProjection?.capacityAndResilience
  );
}

/**
 * Returns true when the report has data the body would render for
 * the given section id. Used to filter dead anchors out of the TOC
 * (the legacy generator emitted an entry per registered section
 * regardless of whether the body would render it).
 *
 * Exported so SiteRagReportView's render layer can derive the same
 * list and the bidirectional agreement test can verify the lists
 * match.
 */
export function hasRenderableContent(sectionId: string, report: Record<string, unknown>): boolean {
  if (ALWAYS_TOC_SECTIONS.has(sectionId)) return true;
  // capacityCreditDeepDive renders on the broader BODY predicate, not just its
  // typed field `powerAnalysis.capacityCreditDeepDive`. Special-case it BEFORE the
  // field-path short-circuit below so the TOC numbers it exactly when the body
  // shows it — otherwise the field-path check drops it when only the deterministic
  // power-basis strip has data, leaving the body heading unnumbered and the
  // following sections' numbers regressed.
  if (sectionId === 'capacityCreditDeepDive') {
    return capacityCreditDeepDiveHasContent(report as unknown as SiteRagReport);
  }
  const reportType = report.reportType as ReportTypeId | undefined;
  const fieldPath = fieldPathForSection(reportType, sectionId);
  if (fieldPath) {
    return hasSectionFieldContent(getReportFieldValue(report, fieldPath));
  }
  const check = SECTION_CONTENT_CHECKS[sectionId];
  if (!check) return true; // unknown id → leave to the body's guards
  return check(report);
}

export interface SectionEntry {
  id: string;
  label: string;
  /** 1 = top-level (default). Reserved for nested headings if a
   *  future report shape introduces them. */
  level: 1 | 2;
}

export interface DeriveTocEntriesInput {
  reportType: ReportTypeId | undefined | null;
  sectionOrder: string[] | undefined;
  /** @deprecated Prefer effectiveVisibility when using live-template layout. */
  sectionVisibility?: Record<string, boolean>;
  /** Merged visibility from resolveReportPresentation (template-first). */
  effectiveVisibility?: Record<string, boolean>;
  reportOverrides?: Record<string, string>;
  templateOverrides?: Record<string, string>;
  /**
   * The report record itself — drives the per-section
   * `hasRenderableContent` filter so the TOC and body agree.
   * Optional for back-compat: when omitted, the content check is
   * skipped and the legacy "every registered section" behaviour
   * applies. New call sites should pass the report.
   */
  report?: Record<string, unknown>;
}

function isTocSectionIdForReportType(
  id: string,
  reportType: ReportTypeId | undefined | null,
  report?: Record<string, unknown>,
): boolean {
  const type = (reportType ?? (report?.reportType as ReportTypeId) ?? 'feasibility') as ReportTypeId;
  if (isAllowedExtraSectionId(type, id)) return true;
  const applicabilityReport = {
    reportType: type,
    gridLedStandbyPlanningProjection: report?.gridLedStandbyPlanningProjection,
    gridLedStandbyPlanningAtGen: report?.gridLedStandbyPlanningAtGen,
    sectionOrder: Array.isArray(report?.sectionOrder)
      ? (report!.sectionOrder as string[])
      : undefined,
  };
  return isSectionApplicableForReportType(id, applicabilityReport);
}

function customBriefTocEntry(
  sectionId: string,
  report: Record<string, unknown>,
  defaultLabel: string,
  reportOverrides?: Record<string, string>,
  templateOverrides?: Record<string, string>,
): SectionEntry | null {
  const qid = parseCustomBriefQuestionSectionId(sectionId);
  if (!qid) return null;
  const answers = report.customBriefAnswers as CustomBriefAnswer[] | undefined;
  const sectionOrder = Array.isArray(report.sectionOrder)
    ? (report.sectionOrder as string[])
    : undefined;
  const answer = answers?.find(a => a.questionId === qid);
  const qNum = resolveCustomBriefQuestionNumber(answers ?? [], qid, sectionOrder);
  const label = answer?.question?.trim()
    ? customBriefQuestionTocLabel(answer.question, qNum)
    : resolveHeading(sectionId, defaultLabel, reportOverrides, templateOverrides);
  const id = answer?.questionSlug?.trim() || sectionId;
  return { id, label, level: 1 };
}

export function deriveTocEntries(input: DeriveTocEntriesInput): SectionEntry[] {
  const { reportType, sectionOrder, sectionVisibility, effectiveVisibility, reportOverrides, templateOverrides, report } = input;
  const visibilityMap = effectiveVisibility ?? sectionVisibility;
  const registry = getSectionsForType(reportType ?? undefined, report);
  const hidden = report ? hiddenReportSectionIds(report as unknown as SiteRagReport) : new Set<string>();
  const labelById = new Map(registry.map(s => [s.sectionId, s.defaultLabel]));

  const ids = sectionOrder && sectionOrder.length > 0
    ? sectionOrder
    : registry.map(s => s.sectionId);

  const seen = new Set<string>();
  const entries: SectionEntry[] = [];
  for (const id of ids) {
    if (seen.has(id)) continue;          // dedupe — sectionOrder regressions could repeat ids
    seen.add(id);
    if (TOC_HIDDEN_IDS.has(id)) continue;
    if (hidden.has(id)) continue;
    if (!isTocSectionIdForReportType(id, reportType, report)) continue;
    if (visibilityMap && visibilityMap[id] === false) continue;
    if (report && !hasRenderableContent(id, report)) continue;
    const defaultLabel = labelById.get(id) ?? rendererSectionById(id)?.label;
    if (reportType === 'custom-brief' && isCustomBriefQuestionSectionId(id) && report) {
      const cbEntry = customBriefTocEntry(
        id,
        report,
        defaultLabel ?? 'Question',
        reportOverrides,
        templateOverrides,
      );
      if (cbEntry) {
        entries.push(cbEntry);
        continue;
      }
    }
    if (!defaultLabel) continue;
    const label = resolveHeading(id, defaultLabel, reportOverrides, templateOverrides);
    entries.push({ id, label, level: 1 });
  }
  return entries;
}

/**
 * Returns the ordered list of section ids the body would actually
 * render — the inverse of the TOC's filter. Used by the bidirectional
 * agreement test to assert the TOC entry list matches the body's
 * rendered headings exactly.
 */
export function effectiveRenderedSections(input: DeriveTocEntriesInput & { report: Record<string, unknown> }): string[] {
  return deriveTocEntries(input).map(e => e.id);
}
